// Copyright (c) 2025 Audere, All Rights Reserved

import {
  createApi,
  fetchBaseQuery,
  QueryStatus,
  type BaseQueryApi,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { useSelector } from "react-redux";

import { type AuthState, type GetRolesData, type PortalApiResult } from "../../types/auth";
import { LogoutReasons, type LogEventData, type LogoutReason } from "../../types/logging";
import { googleLogout } from "@react-oauth/google";
import { logOut } from "../auth/authSlice";
import type { AppDispatch, RootState } from "../store";
import { clearProject } from "../data/projectSlice";

const isProd = () => {
  const host = window.location.hostname;
  return !host.includes(".staging.") && !host.includes("localhost");
};

const apiBaseURL = () => {
  return `https://${
    isProd() ? import.meta.env.VITE_API_BASE_HOST_PROD : import.meta.env.VITE_API_BASE_HOST_STAGING
  }/v1/usw2fig`;
};

const baseQuery = fetchBaseQuery({
  baseUrl: apiBaseURL(),
  mode: "cors",
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    headers.set("Content-Type", "application/json");
    if (!headers.get("X-Audere-API-Token")) {
      const state = getState() as { auth: AuthState };
      const token = state.auth.token;
      if (token) {
        headers.set("X-Audere-API-Token", `${token}`);
      }
    }
    return headers;
  },
});

export const logOutHelper = (
  dispatch: AppDispatch,
  apiSlice: ApiSliceType,
  logoutReason: LogoutReason
) => {
  logEvent(dispatch, {
    event: "userLogout",
    data: { reason: logoutReason },
  } as LogEventData);
  googleLogout();
  dispatch(logOut());
  dispatch(clearProject());
  dispatch(apiSlice.util.resetApiState());
};

export const baseQueryWithReauth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions = {}
) => {
  if (api.endpoint !== "openIdAuthenticate" && api.endpoint !== "logEvent") {
    const getStateT = api.getState as () => { auth: AuthState };
    const state = getStateT();
    const auth = state.auth;

    if (new Date(auth.expires) < new Date(Date.now())) {
      const refreshResult = {
        error: {
          status: "CUSTOM_ERROR",
          error: "Token has expired",
        } as FetchBaseQueryError,
      };
      logOutHelper(api.dispatch, apiSlice, LogoutReasons.TokenExpired);
      return refreshResult;
    }
  }

  const result = await baseQuery(args, api, extraOptions);

  return result;
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: builder => ({
    logEvent: builder.mutation<string, LogEventData>({
      query: args => {
        if (!import.meta.env.VITE_APP_LOG_ID) {
          throw new Error(
            "VITE_APP_LOG_ID is not set so cannot log events. If running locally, add a .env file with this variable included."
          );
        }
        return {
          url: "/portal_log",
          method: "POST",
          body: JSON.stringify({
            timestamp: new Date().toISOString(),
            ...args,
          }),
          headers: [["X-Api-Key", `${import.meta.env.VITE_APP_LOG_ID}`]],
        };
      },
      transformResponse: (rawResult: { uid: string; response_code: string }) => {
        return rawResult.uid;
      },
    }),
    getRoles: builder.query<GetRolesData, string>({
      query: token => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_roles",
          input: {},
        }),
        headers: [["X-Audere-API-Token", `${token}`]],
      }),
      transformResponse: (rawResult: PortalApiResult<GetRolesData>) => {
        return rawResult.output;
      },
    }),
  }),
});

type ApiSliceType = typeof apiSlice;

export const logEvent = (dispatch: AppDispatch, event: LogEventData) => {
  dispatch(apiSlice.endpoints.logEvent.initiate(event));
};

export const { useLazyGetRolesQuery } = apiSlice;

export const useIsFetching = (): boolean => {
  return useSelector((state: RootState) => {
    const apiState = state[apiSlice.reducerPath] as {
      queries?: Record<string, { status?: string }>;
      mutations?: Record<string, { status?: string }>;
    };
    const queriesLoading = Object.values(apiState?.queries ?? {}).some(
      query => query?.status === QueryStatus.pending
    );
    const mutationsLoading = Object.values(apiState?.mutations ?? {}).some(
      mutation => mutation?.status === QueryStatus.pending
    );
    return queriesLoading || mutationsLoading;
  });
};
